import { useEffect, useState, useCallback } from 'react';
import { Card, Pack, PackOpenResult, User } from '../types';
import { packs as packsApi } from '../api';

import CardFlip from '../components/CardFlip';
import RarityBadge from '../components/RarityBadge';

type Phase = 'select' | 'paying' | 'revealing' | 'done';

interface PackOpeningProps {
  user: User;
}

export default function PackOpening({ user }: PackOpeningProps) {
  const [availablePacks, setAvailablePacks] = useState<Pack[]>([]);
  const [selectedPack, setSelectedPack] = useState<Pack | null>(null);
  const [phase, setPhase] = useState<Phase>('select');
  const [result, setResult] = useState<PackOpenResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingPacks, setLoadingPacks] = useState(true);
  const [revealedCount, setRevealedCount] = useState(0);

  useEffect(() => {
    // Show fallback pack immediately so UI is never empty
    const fallbackPack: Pack = {
      id: 1,
      name: 'Standard Pack',
      price_ton: 0.5,
      card_count: 10,
      rarity_weights: { common: 0.6, uncommon: 0.25, rare: 0.12, 'ultra-rare': 0.03 },
      active: true,
    };
    setAvailablePacks([fallbackPack]);
    setSelectedPack(fallbackPack);
    setLoadingPacks(false);

    // Try to fetch real packs in background
    packsApi
      .list()
      .then((res) => {
        if (res.packs.length > 0) {
          setAvailablePacks(res.packs);
          setSelectedPack(res.packs[0]);
        }
      })
      .catch((err) => console.error('Packs fetch failed:', err));
  }, []);

  const handleOpenPack = async () => {
    if (!selectedPack) return;
    setError(null);
    setPhase('paying');

    try {
      const res = await packsApi.open(selectedPack.id, user.tg_id || 'guest_' + Date.now(), user.tg_name || 'Guest');
      setResult(res);
      setRevealedCount(0);
      setPhase('revealing');
    } catch (err) {
      console.error('Pack open error:', err);
      setError(err instanceof Error ? err.message : String(err));
      setPhase('select');
    }
  };

  const handleCardRevealed = useCallback(() => {
    setRevealedCount((n) => n + 1);
  }, []);

  const handleReset = () => {
    setPhase('select');
    setResult(null);
    setRevealedCount(0);
    setError(null);
  };

  if (loadingPacks) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-accent-purple border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error && availablePacks.length === 0) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <div className="text-center space-y-2">
          <p className="text-2xl">⚠️</p>
          <p className="text-white font-semibold">Could not load packs</p>
          <p className="text-red-400 text-xs font-mono break-all">{error}</p>
        </div>
      </div>
    );
  }

  if (phase === 'revealing' && result) {
    const allRevealed = revealedCount >= result.cards.length;
    return (
      <div className="h-full flex flex-col">
        <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
          <h2 className="text-white font-bold text-lg">
            {selectedPack?.name} Opening
          </h2>
          <span className="text-gray-400 text-sm">
            {Math.min(revealedCount, result.cards.length)}/{result.cards.length}
          </span>
        </div>

        <div className="flex-1 scroll-area p-4">
          {/* Card grid — 5 columns for cards 1-9, last card spans 2 cols centered */}
          <div className="grid grid-cols-5 gap-2 mb-8" style={{ gridAutoRows: 'auto' }}>
            {result.cards.map((card, i) => {
              const isLast = i === result.cards.length - 1;
              const delay = i * 400; // stagger each card 400ms
              return (
                <CardFlip
                  key={card.id + '-' + i}
                  card={card}
                  index={i}
                  isLast={isLast}
                  revealDelay={delay}
                  onRevealed={handleCardRevealed}
                />
              );
            })}
          </div>

          {/* All revealed: show summary */}
          {allRevealed && (
            <div className="space-y-3 animate-[card-appear_0.5s_ease-out]">
              <h3 className="text-white font-bold text-center text-lg">Pack Summary</h3>
              <div className="space-y-2">
                {result.cards.map((card, i) => (
                  <PullRow key={i} card={card} index={i} isLast={i === result.cards.length - 1} />
                ))}
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  onClick={handleReset}
                  className="flex-1 py-3 bg-surface-elevated text-white font-semibold rounded-xl"
                >
                  Open Another
                </button>
                <button
                  onClick={handleReset}
                  className="flex-1 py-3 bg-accent-purple text-white font-semibold rounded-xl"
                >
                  View Collection
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full scroll-area px-4 py-4 space-y-4">
      <h2 className="text-white font-bold text-xl">Pack Opening</h2>

      {error && (
        <div className="bg-red-900/30 border border-red-700/30 rounded-xl p-3 text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* Pack selector */}
      <div className="space-y-2">
        {availablePacks.map((pack) => {
          const weights = (typeof pack.rarity_weights === 'string' ? JSON.parse(pack.rarity_weights) : pack.rarity_weights) as Record<string, number>;
          const isSelected = selectedPack?.id === pack.id;
          return (
            <button
              key={pack.id}
              onClick={() => setSelectedPack(pack)}
              className={`w-full text-left p-4 rounded-2xl border transition-all ${
                isSelected
                  ? 'border-accent-purple bg-accent-purple/10 shadow-lg shadow-accent-purple/20'
                  : 'border-white/5 bg-surface-card'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="flex items-center gap-2">
                    {isSelected && (
                      <span className="w-2 h-2 bg-accent-purple rounded-full" />
                    )}
                    <h3 className="text-white font-bold">{pack.name}</h3>
                  </div>
                  <p className="text-gray-400 text-sm">{pack.card_count} cards</p>
                </div>
                <p className="text-accent-gold font-bold text-xl">
                  💎 {pack.price_ton}
                </p>
              </div>

              {/* Rarity breakdown */}
              <div className="flex gap-3">
                {Object.entries(weights).map(([rarity, pct]) => (
                  <div key={rarity} className="text-center">
                    <p className="text-gray-500 text-xs capitalize">
                      {rarity === 'ultra-rare' ? 'UR' : rarity.slice(0, 1).toUpperCase() + rarity.slice(1, 4)}
                    </p>
                    <p className="text-gray-300 text-xs font-medium">{Math.round(pct * 100)}%</p>
                  </div>
                ))}
              </div>
            </button>
          );
        })}

        {availablePacks.length === 0 && (
          <div className="text-center py-10 text-gray-500">
            No packs available. Ask an admin to seed the catalog.
          </div>
        )}
      </div>

      {/* Preview */}
      {selectedPack && (
        <div className="bg-gradient-to-br from-accent-purple/20 to-surface-elevated rounded-2xl p-5 border border-accent-purple/20 space-y-4">
          <div className="text-center">
            <div className="text-6xl mb-2">📦</div>
            <h3 className="text-white font-bold text-xl">{selectedPack.name}</h3>
            <p className="text-gray-400 text-sm">
              {selectedPack.card_count} cards · 1 guaranteed rare
            </p>
          </div>

          {/* Card back preview */}
          <div className="flex justify-center gap-1.5">
            {Array.from({ length: selectedPack.card_count }).map((_, i) => (
              <div
                key={i}
                className={`flex-1 max-w-[32px] aspect-[2.5/3.5] rounded-md border ${
                  i === selectedPack.card_count - 1
                    ? 'border-amber-500/50 bg-gradient-to-br from-amber-900/40 to-yellow-700/20'
                    : 'border-blue-700/30 bg-blue-900/40'
                }`}
              />
            ))}
          </div>

          <button
            onClick={handleOpenPack}
            disabled={phase === 'paying'}
            className="w-full py-4 bg-gradient-to-r from-accent-purple to-purple-600 hover:from-accent-purple/90 hover:to-purple-700 disabled:opacity-50 text-white font-bold text-lg rounded-xl transition-all shadow-xl shadow-accent-purple/30 active:scale-95"
          >
            {phase === 'paying' ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Opening…
              </span>
            ) : (
              `Open Pack · 💎 ${selectedPack.price_ton} TON`
            )}
          </button>

          <p className="text-center text-gray-500 text-xs">
            You'll be asked to provide a TON transaction hash after sending
          </p>
        </div>
      )}
    </div>
  );
}

function PullRow({ card, index, isLast }: { card: Card; index: number; isLast: boolean }) {
  return (
    <div
      className={`flex items-center gap-3 p-2 rounded-xl ${
        isLast ? 'bg-amber-900/20 border border-amber-700/30' : 'bg-surface-card'
      }`}
    >
      <span className="text-gray-500 text-sm w-4">{index + 1}</span>
      <img
        src={card.image_url}
        alt={card.pokemon_name}
        className="w-8 h-11 object-cover rounded"
      />
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium truncate">{card.pokemon_name}</p>
        <p className="text-gray-500 text-xs truncate">{card.set_name}</p>
      </div>
      <RarityBadge rarity={card.rarity} />
      {isLast && <span className="text-amber-400">⭐</span>}
    </div>
  );
}
